export interface Lesson {
  id: number;
  title: string;
  content: {
    introduction: string;
    sections: {
      title: string;
      content: string;
      keyPoints?: string[];
      example?: string;
      commonError?: {
        error: string;
        correction: string;
      };
    }[];
  };
}

export const lessonsData: Lesson[] = [
  {
    id: 1,
    title: "Sistema Weider",
    content: {
      introduction: "El Sistema Weider es un conjunto de principios de entrenamiento desarrollados por Joe Weider que revolucionó el culturismo moderno. Basado en alto volumen, división muscular y técnicas de intensidad.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "El sistema se fundamenta en la especialización muscular mediante división de grupos musculares, alto volumen de trabajo y uso de técnicas de intensidad para maximizar el estímulo hipertrófico. Prioriza el trabajo aislado y la congestión muscular.",
          keyPoints: [
            "División muscular por grupos (split routine)",
            "Alto volumen de series y ejercicios por músculo",
            "Uso de técnicas de intensidad (superseries, drop sets)",
            "Enfoque en la conexión mente-músculo",
            "Periodización intuitiva basada en sensaciones"
          ]
        },
        {
          title: "Volumen, Frecuencia e Intensidad",
          content: "Volumen: 12-20 series por grupo muscular por sesión. Frecuencia: 1-2 veces por semana por músculo. Intensidad: Moderada-alta (70-85% 1RM), con énfasis en fallo muscular y técnicas avanzadas.",
          keyPoints: [
            "3-5 ejercicios por grupo muscular",
            "3-4 series por ejercicio",
            "8-12 repeticiones por serie (hipertrofia)",
            "Descansos de 60-90 segundos",
            "Entrenamiento 5-6 días por semana"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Practicantes intermedios-avanzados con buena base de fuerza, capacidad de recuperación desarrollada y tiempo disponible para entrenar frecuentemente. Personas que responden bien al volumen y buscan desarrollo muscular específico.",
          keyPoints: [
            "Mínimo 1-2 años de experiencia",
            "Buena técnica en ejercicios básicos",
            "Capacidad de entrenar 5-6 días/semana",
            "Objetivo primario: hipertrofia",
            "Buena recuperación y nutrición"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Permite gran volumen de trabajo específico por músculo, facilita el enfoque mental en grupos concretos, y ofrece flexibilidad para atacar puntos débiles. Ideal para culturismo y desarrollo estético.",
          keyPoints: [
            "Alto volumen de trabajo especializado",
            "Flexibilidad para priorizar músculos rezagados",
            "Variedad de ejercicios y técnicas",
            "Buena congestión y sensación muscular",
            "Adaptable a diferentes objetivos estéticos"
          ]
        },
        {
          title: "Limitaciones",
          content: "Requiere mucho tiempo de entrenamiento, puede generar fatiga acumulada, menor frecuencia de estímulo por músculo, y riesgo de sobreentrenamiento si no se gestiona bien la recuperación.",
          keyPoints: [
            "Demanda 5-6 días de entrenamiento semanal",
            "Baja frecuencia por grupo muscular (1x/semana)",
            "Puede generar fatiga sistémica acumulada",
            "No óptimo para ganancia de fuerza máxima",
            "Requiere experiencia para autoregular volumen"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal",
          content: "División clásica de 5 días con enfoque en hipertrofia y volumen moderado-alto.",
          example: "Lunes - Pecho:\n• Press banca: 4x8-10\n• Press inclinado mancuernas: 4x10-12\n• Aperturas: 3x12-15\n• Fondos: 3x10-12\n• Cruces poleas: 3x15\n\nMartes - Espalda:\n• Dominadas: 4x8-10\n• Remo barra: 4x8-10\n• Remo mancuerna: 3x10-12\n• Jalón agarre cerrado: 3x12\n• Pullover: 3x15\n\nMiércoles - Pierna:\n• Sentadilla: 4x8-10\n• Prensa: 4x12-15\n• Peso muerto rumano: 3x10-12\n• Curl femoral: 3x12-15\n• Extensiones: 3x15\n\nJueves - Hombro:\n• Press militar: 4x8-10\n• Elevaciones laterales: 4x12-15\n• Remo al mentón: 3x10-12\n• Pájaros: 3x15\n• Press Arnold: 3x10-12\n\nViernes - Brazos:\n• Curl barra: 4x10-12\n• Press francés: 4x10-12\n• Curl martillo: 3x12\n• Fondos tríceps: 3x12\n• Curl concentrado: 3x15\n• Extensión polea: 3x15"
        }
      ]
    }
  },
  {
    id: 2,
    title: "Heavy Duty (Mike Mentzer)",
    content: {
      introduction: "Sistema de entrenamiento de alta intensidad y bajo volumen desarrollado por Mike Mentzer. Se basa en la filosofía de que el músculo necesita un estímulo intenso pero breve, seguido de recuperación prolongada.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "Heavy Duty se fundamenta en la premisa de que el crecimiento muscular requiere intensidad máxima, no volumen excesivo. Una serie llevada al fallo absoluto con técnicas de intensidad genera el estímulo necesario. Más entrenamiento no es mejor; la recuperación es donde ocurre el crecimiento.",
          keyPoints: [
            "Una serie al fallo absoluto es suficiente",
            "Intensidad máxima sobre volumen",
            "Recuperación prolongada entre sesiones",
            "Progresión en carga como indicador de adaptación",
            "Menos es más: evitar sobreentrenamiento"
          ]
        },
        {
          title: "Volumen, Frecuencia e Intensidad",
          content: "Volumen: 1-2 series por ejercicio, 4-6 series totales por músculo. Frecuencia: Cada músculo cada 5-7 días. Intensidad: Máxima (85-95% 1RM), siempre al fallo muscular con técnicas avanzadas.",
          keyPoints: [
            "1-2 ejercicios por grupo muscular",
            "1 serie efectiva al fallo por ejercicio",
            "6-8 repeticiones típicamente",
            "Descansos de 3-5 minutos entre series",
            "Entrenamiento 2-3 días por semana"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Practicantes avanzados con excelente técnica, alta tolerancia al dolor, capacidad mental para alcanzar el fallo real, y que responden bien a baja frecuencia. No apto para principiantes.",
          keyPoints: [
            "Mínimo 2-3 años de experiencia",
            "Técnica impecable en ejercicios básicos",
            "Alta intensidad mental y física",
            "Buena capacidad de recuperación",
            "Personas que sobreentrenan con volumen alto"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Eficiencia temporal extrema, menor desgaste articular por bajo volumen, ideal para personas con poca capacidad de recuperación o tiempo limitado. Enseña a entrenar con verdadera intensidad.",
          keyPoints: [
            "Sesiones de 30-45 minutos",
            "Menor fatiga sistémica acumulada",
            "Menos desgaste articular",
            "Ideal para recuperadores lentos",
            "Enseña intensidad real vs volumen basura"
          ]
        },
        {
          title: "Limitaciones",
          content: "Requiere experiencia para ejecutar correctamente, alta exigencia mental, no permite mucha variedad, y algunos usuarios necesitan más frecuencia de estímulo para crecer óptimamente.",
          keyPoints: [
            "No apto para principiantes",
            "Requiere compañero para forzadas/negativas",
            "Poca variedad de ejercicios",
            "Algunos no responden bien a baja frecuencia",
            "Difícil progresar en fuerza con 1 serie"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal",
          content: "División de 3 días con descanso entre cada sesión. Cada ejercicio: 1 serie de calentamiento + 1 serie al fallo absoluto.",
          example: "Día 1 - Pecho/Espalda:\n• Press banca: 1x6-8 al fallo + 2 forzadas\n• Dominadas lastre: 1x6-8 al fallo + negativa\n• Press inclinado: 1x8-10 al fallo\n• Remo barra: 1x6-8 al fallo + forzadas\n\nDía 2 - Descanso\n\nDía 3 - Pierna:\n• Sentadilla: 1x8-10 al fallo + rest-pause\n• Peso muerto rumano: 1x8-10 al fallo\n• Extensiones: 1x10-12 al fallo + drop set\n• Curl femoral: 1x10-12 al fallo\n\nDía 4 - Descanso\n\nDía 5 - Hombro/Brazos:\n• Press militar: 1x6-8 al fallo + forzadas\n• Elevaciones laterales: 1x10-12 al fallo\n• Curl barra: 1x6-8 al fallo + negativa\n• Fondos tríceps: 1x8-10 al fallo + forzadas\n\nDías 6-7 - Descanso\n\nNota: Cada serie precedida de calentamiento progresivo. Fallo absoluto significa imposibilidad de completar otra repetición con técnica."
        }
      ]
    }
  },
  {
    id: 3,
    title: "HIT Clásico (High Intensity Training)",
    content: {
      introduction: "El HIT clásico, popularizado por Arthur Jones (creador de Nautilus), es el precursor del Heavy Duty. Enfatiza entrenamientos breves, intensos e infrecuentes con ejercicios básicos multiarticulares.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "El HIT se basa en estimular el músculo con la mínima dosis efectiva de ejercicio de alta intensidad, permitiendo máxima recuperación. Prioriza ejercicios compuestos, movimientos lentos y controlados, y progresión constante en carga.",
          keyPoints: [
            "Intensidad sobre volumen y frecuencia",
            "Ejercicios multiarticulares prioritarios",
            "Tempo lento y controlado (4-2-4)",
            "Progresión lineal en carga",
            "Recuperación completa entre sesiones"
          ]
        },
        {
          title: "Volumen, Frecuencia e Intensidad",
          content: "Volumen: 1-3 series por ejercicio, 6-9 series totales por sesión. Frecuencia: 2-3 sesiones semanales de cuerpo completo. Intensidad: Alta (80-90% 1RM), llevando la última serie al fallo muscular.",
          keyPoints: [
            "8-12 ejercicios por sesión (full body)",
            "1-2 series por ejercicio",
            "8-12 repeticiones con tempo lento",
            "Descansos de 2-3 minutos",
            "2-3 días por semana no consecutivos"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Principiantes avanzados e intermedios que buscan eficiencia, personas con tiempo limitado, recuperadores lentos, y quienes buscan equilibrio entre fuerza e hipertrofia sin especializarse en culturismo.",
          keyPoints: [
            "Desde 6 meses de experiencia",
            "Personas con tiempo limitado (3h/semana)",
            "Recuperación lenta o media",
            "Objetivo: fuerza funcional + masa",
            "Buscan simplicidad y eficiencia"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Excelente relación tiempo-resultados, menor riesgo de sobreentrenamiento, ideal para mantener fuerza y masa con mínimo tiempo, y permite combinar con otros deportes o actividades.",
          keyPoints: [
            "Sesiones de 45-60 minutos",
            "Alta frecuencia de estímulo por músculo (2-3x/semana)",
            "Bajo riesgo de sobreentrenamiento",
            "Compatible con otros deportes",
            "Progresión clara y medible"
          ]
        },
        {
          title: "Limitaciones",
          content: "No permite alto volumen de trabajo específico por músculo, puede ser insuficiente para culturistas avanzados, y el tempo lento puede limitar cargas absolutas en algunos ejercicios.",
          keyPoints: [
            "Volumen limitado para especialización muscular",
            "No óptimo para culturismo competitivo",
            "Tempo lento reduce cargas máximas",
            "Poca variedad de ejercicios",
            "Puede aburrir por falta de variación"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal",
          content: "Rutina de cuerpo completo 3 días por semana. Tempo 4-0-2 (4 seg excéntrica, 0 pausa, 2 seg concéntrica).",
          example: "Lunes/Miércoles/Viernes:\n\n• Sentadilla: 2x8-10 (tempo 4-0-2)\n• Peso muerto: 1x8-10 (última al fallo)\n• Press banca: 2x8-10\n• Remo barra: 2x8-10\n• Press militar: 1x8-10\n• Dominadas: 2x8-10 (lastre si es necesario)\n• Fondos: 1x8-10\n• Curl barra: 1x8-10\n• Extensión tríceps: 1x8-10\n• Elevaciones laterales: 1x12-15\n• Plancha: 1x60-90 seg\n\nProgresión: Cuando se completan las repeticiones máximas en todas las series con buena técnica, aumentar 2.5-5kg la próxima sesión.\n\nDescanso: Mínimo 48h entre sesiones. Si no hay recuperación completa, reducir a 2 sesiones semanales."
        }
      ]
    }
  },
  {
    id: 4,
    title: "Push Pull Legs (PPL)",
    content: {
      introduction: "División de entrenamiento que agrupa músculos por función mecánica: empuje (pecho, hombros, tríceps), tirón (espalda, bíceps) y pierna. Permite alta frecuencia con recuperación adecuada.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "El PPL agrupa músculos que trabajan sinérgicamente en patrones de movimiento naturales. Esto permite entrenar cada grupo 2 veces por semana con volumen moderado-alto, optimizando frecuencia de estímulo y recuperación.",
          keyPoints: [
            "Agrupación por función mecánica",
            "Alta frecuencia (2x/semana por músculo)",
            "Volumen distribuido en dos sesiones",
            "Permite especialización dentro de cada día",
            "Flexibilidad en ejercicios y volumen"
          ]
        },
        {
          title: "Volumen, Frecuencia e Intensidad",
          content: "Volumen: 12-18 series por grupo muscular por semana (6-9 por sesión). Frecuencia: 2 veces por semana por músculo (6 días totales). Intensidad: Moderada-alta (70-85% 1RM).",
          keyPoints: [
            "3-5 ejercicios por grupo muscular por día",
            "3-4 series por ejercicio",
            "6-12 repeticiones según ejercicio",
            "Descansos de 90-120 segundos",
            "Ciclo de 6 días (PPL-PPL) + 1 descanso"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Intermedios-avanzados que pueden entrenar 6 días por semana, buscan equilibrio entre volumen y frecuencia, y quieren flexibilidad para ajustar énfasis según objetivos (fuerza, hipertrofia, resistencia).",
          keyPoints: [
            "Mínimo 1 año de experiencia",
            "Disponibilidad de 6 días/semana",
            "Buena capacidad de recuperación",
            "Objetivo: hipertrofia + fuerza",
            "Buscan estructura pero con flexibilidad"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Excelente frecuencia de estímulo (2x/semana), volumen distribuido evita fatiga excesiva por sesión, permite priorizar músculos rezagados, y es altamente personalizable según nivel y objetivos.",
          keyPoints: [
            "Frecuencia óptima para hipertrofia (2x/semana)",
            "Sesiones de duración moderada (60-90 min)",
            "Flexibilidad para ajustar volumen",
            "Permite periodización dentro del ciclo",
            "Buena para fuerza e hipertrofia simultáneas"
          ]
        },
        {
          title: "Limitaciones",
          content: "Requiere 6 días de entrenamiento semanal, puede generar fatiga acumulada si no se gestiona bien, y algunos músculos pequeños (bíceps, tríceps) trabajan en múltiples días.",
          keyPoints: [
            "Demanda 6 días de compromiso semanal",
            "Brazos trabajan 4 días (push + pull)",
            "Puede generar fatiga si volumen es excesivo",
            "Requiere buena gestión de intensidad",
            "No ideal para principiantes"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal",
          content: "Ciclo PPL-PPL con énfasis en fuerza el primer ciclo e hipertrofia el segundo.",
          example: "Día 1 - Push (Fuerza):\n• Press banca: 4x4-6 (85-90%)\n• Press militar: 4x6-8\n• Press inclinado: 3x8-10\n• Elevaciones laterales: 3x12-15\n• Fondos: 3x8-10\n• Extensión tríceps: 3x10-12\n\nDía 2 - Pull (Fuerza):\n• Peso muerto: 4x4-6 (85-90%)\n• Dominadas lastre: 4x6-8\n• Remo barra: 3x8-10\n• Remo mancuerna: 3x10-12\n• Curl barra: 3x8-10\n• Curl martillo: 3x10-12\n\nDía 3 - Legs (Fuerza):\n• Sentadilla: 4x4-6 (85-90%)\n• Peso muerto rumano: 3x8-10\n• Prensa: 3x10-12\n• Curl femoral: 3x10-12\n• Extensiones: 3x12-15\n• Gemelos: 4x15-20\n\nDía 4 - Push (Hipertrofia):\n• Press banca: 4x8-10 (75-80%)\n• Press mancuernas: 4x10-12\n• Aperturas: 3x12-15\n• Press Arnold: 3x10-12\n• Elevaciones laterales: 4x15-20\n• Tríceps polea: 3x12-15\n\nDía 5 - Pull (Hipertrofia):\n• Dominadas: 4x8-10\n• Remo T: 4x10-12\n• Jalón agarre cerrado: 3x12-15\n• Face pulls: 3x15-20\n• Curl inclinado: 3x10-12\n• Curl concentrado: 3x12-15\n\nDía 6 - Legs (Hipertrofia):\n• Sentadilla frontal: 4x8-10\n• Zancadas: 3x10-12 por pierna\n• Prensa: 3x12-15\n• Curl femoral: 4x12-15\n• Extensiones: 3x15-20\n• Gemelos sentado: 4x20-25\n\nDía 7 - Descanso"
        }
      ]
    }
  },
  {
    id: 5,
    title: "Full Body (Cuerpo Completo)",
    content: {
      introduction: "Entrenamiento de todos los grupos musculares principales en cada sesión. Ideal para maximizar frecuencia de estímulo con volumen moderado por músculo por sesión.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "El Full Body se basa en estimular todos los grupos musculares frecuentemente (3x/semana) con volumen moderado por sesión. Prioriza ejercicios multiarticulares y permite alta frecuencia de práctica técnica.",
          keyPoints: [
            "Todos los músculos cada sesión",
            "Alta frecuencia de estímulo (3x/semana)",
            "Volumen moderado por músculo por sesión",
            "Énfasis en ejercicios compuestos",
            "Ideal para ganancia de fuerza y técnica"
          ]
        },
        {
          title: "Volumen, Frecuencia e Intensidad",
          content: "Volumen: 12-18 series por grupo muscular por semana (4-6 por sesión). Frecuencia: 3 veces por semana por músculo. Intensidad: Moderada-alta (75-85% 1RM), raramente al fallo.",
          keyPoints: [
            "1-2 ejercicios por grupo muscular por sesión",
            "2-4 series por ejercicio",
            "5-10 repeticiones según objetivo",
            "Descansos de 2-3 minutos",
            "3 días por semana no consecutivos"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Principiantes, intermedios, atletas que necesitan fuerza funcional, personas con tiempo limitado, y quienes buscan mantener masa muscular mientras entrenan otros deportes.",
          keyPoints: [
            "Ideal para principiantes (0-2 años)",
            "Atletas de otros deportes",
            "Personas con 3-4 días/semana disponibles",
            "Objetivo: fuerza general + masa",
            "Buscan eficiencia y funcionalidad"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Máxima frecuencia de estímulo por músculo, excelente para ganancia de fuerza, permite practicar técnica frecuentemente, flexible en días de entrenamiento, y bajo riesgo de sobreentrenamiento.",
          keyPoints: [
            "Frecuencia óptima (3x/semana por músculo)",
            "Excelente para ganancia de fuerza",
            "Práctica técnica frecuente",
            "Sesiones de 60-75 minutos",
            "Flexible: puedes faltar un día sin perder frecuencia"
          ]
        },
        {
          title: "Limitaciones",
          content: "Volumen limitado por músculo por sesión, sesiones largas si se añaden muchos ejercicios, puede ser fatigante sistémicamente, y no permite mucha especialización muscular.",
          keyPoints: [
            "Volumen limitado por sesión por músculo",
            "Sesiones pueden ser largas (90+ min)",
            "Fatiga sistémica si volumen es alto",
            "No óptimo para culturismo avanzado",
            "Requiere priorización de ejercicios"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal",
          content: "Tres sesiones semanales con variación de ejercicios y rangos de repeticiones.",
          example: "Lunes (Fuerza):\n• Sentadilla: 4x5 (85%)\n• Press banca: 4x5 (85%)\n• Peso muerto: 3x5 (85%)\n• Dominadas: 3x6-8\n• Press militar: 3x6-8\n• Remo barra: 3x6-8\n• Plancha: 2x45-60 seg\n\nMiércoles (Hipertrofia):\n• Peso muerto rumano: 4x8-10\n• Press inclinado: 4x8-10\n• Sentadilla frontal: 3x8-10\n• Remo mancuerna: 3x10-12\n• Fondos: 3x8-10\n• Curl barra: 2x10-12\n• Elevaciones laterales: 2x12-15\n\nViernes (Mixto):\n• Sentadilla: 4x6-8 (80%)\n• Press banca: 4x6-8 (80%)\n• Peso muerto convencional: 3x6-8\n• Dominadas: 3x8-10\n• Press Arnold: 3x8-10\n• Remo T: 3x8-10\n• Extensión tríceps: 2x10-12\n• Curl martillo: 2x10-12\n\nProgresión: Aumentar carga cuando se completan todas las series con buena técnica. Alternar énfasis cada 4-6 semanas."
        }
      ]
    }
  },
  {
    id: 6,
    title: "Upper Lower (Torso-Pierna)",
    content: {
      introduction: "División que separa entrenamiento de tren superior e inferior. Permite alta frecuencia (2x/semana por músculo) con volumen moderado-alto y recuperación adecuada.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "El Upper/Lower divide el cuerpo en dos grandes bloques, permitiendo entrenar cada uno 2 veces por semana. Ofrece equilibrio óptimo entre frecuencia, volumen y recuperación, siendo versátil para múltiples objetivos.",
          keyPoints: [
            "División simple: torso vs pierna",
            "Frecuencia óptima (2x/semana)",
            "Volumen moderado-alto por sesión",
            "Permite especialización dentro de cada bloque",
            "Versátil para fuerza e hipertrofia"
          ]
        },
        {
          title: "Volumen, Frecuencia e Intensidad",
          content: "Volumen: 12-20 series por grupo muscular por semana (6-10 por sesión). Frecuencia: 2 veces por semana por músculo (4 días totales). Intensidad: Moderada-alta (70-85% 1RM).",
          keyPoints: [
            "4-6 ejercicios por sesión de torso",
            "4-5 ejercicios por sesión de pierna",
            "3-4 series por ejercicio",
            "5-12 repeticiones según ejercicio",
            "4 días por semana (2 upper, 2 lower)"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Intermedios-avanzados que pueden entrenar 4 días por semana, buscan equilibrio entre fuerza e hipertrofia, y quieren estructura simple pero efectiva. Excelente para powerlifters y atletas de fuerza.",
          keyPoints: [
            "Desde 6 meses de experiencia",
            "Disponibilidad de 4 días/semana",
            "Objetivo: fuerza + hipertrofia equilibrada",
            "Ideal para powerlifting",
            "Buscan simplicidad y efectividad"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Excelente frecuencia sin exceso de días de entrenamiento, permite alto volumen sin sesiones excesivamente largas, versátil para periodización, y recuperación adecuada entre sesiones del mismo bloque.",
          keyPoints: [
            "Frecuencia óptima con solo 4 días/semana",
            "Sesiones de 60-90 minutos",
            "Permite periodización fácil",
            "Recuperación de 72-96h por bloque",
            "Excelente para fuerza e hipertrofia"
          ]
        },
        {
          title: "Limitaciones",
          content: "Sesiones de pierna pueden ser muy exigentes, requiere 4 días de compromiso semanal, y algunos músculos pequeños pueden necesitar más frecuencia para responder óptimamente.",
          keyPoints: [
            "Sesiones de pierna muy demandantes",
            "Requiere 4 días de compromiso",
            "Puede ser insuficiente para brazos en algunos casos",
            "No permite tanta especialización como PPL",
            "Fatiga de pierna puede afectar rendimiento general"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal",
          content: "Cuatro sesiones semanales con variación de intensidad entre primera y segunda sesión de cada bloque.",
          example: "Lunes - Upper A (Fuerza):\n• Press banca: 4x4-6 (85-90%)\n• Remo barra: 4x6-8\n• Press militar: 3x6-8\n• Dominadas lastre: 3x6-8\n• Fondos: 3x8-10\n• Curl barra: 2x8-10\n• Face pulls: 2x15-20\n\nMartes - Lower A (Fuerza):\n• Sentadilla: 4x4-6 (85-90%)\n• Peso muerto rumano: 3x6-8\n• Prensa: 3x8-10\n• Curl femoral: 3x10-12\n• Extensiones: 3x10-12\n• Gemelos parado: 4x12-15\n\nJueves - Upper B (Hipertrofia):\n• Press inclinado: 4x8-10\n• Remo mancuerna: 4x10-12\n• Press Arnold: 3x10-12\n• Jalón agarre cerrado: 3x10-12\n• Aperturas: 3x12-15\n• Curl inclinado: 3x10-12\n• Extensión tríceps: 3x12-15\n• Elevaciones laterales: 3x15-20\n\nViernes - Lower B (Hipertrofia):\n• Peso muerto convencional: 4x6-8\n• Sentadilla frontal: 3x8-10\n• Zancadas: 3x10-12 por pierna\n• Curl femoral: 4x12-15\n• Extensiones: 3x15-20\n• Hip thrust: 3x12-15\n• Gemelos sentado: 4x20-25\n\nSábado/Domingo - Descanso"
        }
      ]
    }
  },
  {
    id: 7,
    title: "Periodización Lineal",
    content: {
      introduction: "Modelo de periodización que progresa de forma lineal desde alto volumen/baja intensidad hacia bajo volumen/alta intensidad a lo largo de un mesociclo. Clásica en powerlifting y deportes de fuerza.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "La periodización lineal se basa en desarrollar capacidades de forma secuencial: primero hipertrofia y resistencia muscular, luego fuerza-resistencia, y finalmente fuerza máxima. Cada fase prepara para la siguiente.",
          keyPoints: [
            "Progresión lineal de volumen a intensidad",
            "Fases claramente diferenciadas",
            "Cada fase prepara la siguiente",
            "Pico de rendimiento al final del ciclo",
            "Ideal para principiantes-intermedios"
          ]
        },
        {
          title: "Estructura de Fases",
          content: "Fase 1 (4 semanas): Hipertrofia - 3-4x12-15 reps (65-70% 1RM). Fase 2 (4 semanas): Fuerza-Resistencia - 4x8-10 reps (75-80% 1RM). Fase 3 (3 semanas): Fuerza - 4-5x4-6 reps (85-90% 1RM). Fase 4 (1 semana): Pico - 3-5x1-3 reps (90-95% 1RM).",
          keyPoints: [
            "Mesociclo típico: 12-16 semanas",
            "Volumen decrece, intensidad aumenta",
            "Descansos aumentan progresivamente",
            "Ejercicios se mantienen constantes",
            "Test de 1RM al final del ciclo"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Principiantes-intermedios en deportes de fuerza, powerlifters, personas que compiten y necesitan pico de rendimiento en fecha específica, y quienes responden bien a estructura predefinida.",
          keyPoints: [
            "Principiantes en periodización",
            "Powerlifters y levantadores olímpicos",
            "Personas que compiten",
            "Buscan pico de rendimiento específico",
            "Prefieren estructura clara y predefinida"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Estructura simple y fácil de seguir, progresión clara y medible, excelente para ganancia de fuerza, permite pico de rendimiento predecible, y reduce riesgo de estancamiento a corto plazo.",
          keyPoints: [
            "Muy fácil de planificar y seguir",
            "Progresión clara semana a semana",
            "Excelente para ganancia de fuerza",
            "Pico de rendimiento predecible",
            "Ideal para preparar competiciones"
          ]
        },
        {
          title: "Limitaciones",
          content: "Puede ser monótona, no permite mucha flexibilidad, algunos usuarios pierden hipertrofia en fases de fuerza máxima, y requiere deload/descarga después del pico.",
          keyPoints: [
            "Puede resultar monótona",
            "Poca flexibilidad para ajustes",
            "Algunos pierden masa en fase de fuerza",
            "Requiere descarga post-pico",
            "No óptima para hipertrofia continua"
          ]
        },
        {
          title: "Ejemplo Práctico de Mesociclo",
          content: "Ejemplo de periodización lineal de 12 semanas para sentadilla, press banca y peso muerto.",
          example: "Semanas 1-4 (Hipertrofia):\n• Sentadilla: 4x12 @ 65-70%\n• Press banca: 4x12 @ 65-70%\n• Peso muerto: 3x12 @ 65-70%\n• Descanso: 90 segundos\n\nSemanas 5-8 (Fuerza-Resistencia):\n• Sentadilla: 4x8 @ 75-80%\n• Press banca: 4x8 @ 75-80%\n• Peso muerto: 4x8 @ 75-80%\n• Descanso: 2 minutos\n\nSemanas 9-11 (Fuerza):\n• Sentadilla: 5x5 @ 85%\n• Press banca: 5x5 @ 85%\n• Peso muerto: 4x5 @ 85%\n• Descanso: 3-4 minutos\n\nSemana 12 (Pico/Test):\n• Lunes: Sentadilla 1RM\n• Miércoles: Press banca 1RM\n• Viernes: Peso muerto 1RM\n\nProgresión: Aumentar 2.5-5kg cada semana dentro de cada fase si se completan todas las series.\n\nPost-ciclo: 1 semana de descarga (50-60% volumen e intensidad) antes de iniciar nuevo ciclo."
        }
      ]
    }
  },
  {
    id: 8,
    title: "Periodización Ondulante",
    content: {
      introduction: "Modelo de periodización que varía volumen e intensidad de forma no lineal, alternando diferentes cualidades (fuerza, hipertrofia, potencia) dentro del mismo microciclo o semana.",
      sections: [
        {
          title: "Filosofía de Entrenamiento",
          content: "La periodización ondulante se basa en entrenar múltiples cualidades simultáneamente mediante variación frecuente de volumen e intensidad. Evita adaptación específica y mantiene todas las cualidades activas.",
          keyPoints: [
            "Variación frecuente de estímulos",
            "Múltiples cualidades en mismo microciclo",
            "Evita adaptación y estancamiento",
            "Mayor flexibilidad que lineal",
            "Permite autoregulación"
          ]
        },
        {
          title: "Tipos de Ondulación",
          content: "DUP (Daily Undulating Periodization): Varía cada sesión. WUP (Weekly Undulating Periodization): Varía cada semana. Ejemplo DUP: Lunes fuerza (5x5), Miércoles hipertrofia (4x10), Viernes potencia (5x3). Ejemplo WUP: Semana 1 fuerza, Semana 2 hipertrofia, Semana 3 potencia.",
          keyPoints: [
            "DUP: variación diaria (más común)",
            "WUP: variación semanal",
            "Permite trabajar todo el espectro",
            "Reduce monotonía del entrenamiento",
            "Adaptable según recuperación"
          ]
        },
        {
          title: "Perfil de Usuario Ideal",
          content: "Intermedios-avanzados que se estancan con lineal, atletas que necesitan múltiples cualidades, personas que se aburren con rutinas monótonas, y quienes tienen buena capacidad de autoregulación.",
          keyPoints: [
            "Mínimo 1-2 años de experiencia",
            "Personas que se estancan fácilmente",
            "Atletas de deportes mixtos",
            "Buscan variedad sin perder estructura",
            "Buena capacidad de escuchar al cuerpo"
          ]
        },
        {
          title: "Ventajas Reales",
          content: "Reduce estancamientos, mantiene todas las cualidades activas, permite flexibilidad según recuperación, menos monótona que lineal, y puede generar mejores resultados en intermedios-avanzados.",
          keyPoints: [
            "Menor riesgo de estancamiento",
            "Mantiene fuerza e hipertrofia simultáneas",
            "Más entretenida y variada",
            "Permite ajustes según estado diario",
            "Mejor para atletas de deportes mixtos"
          ]
        },
        {
          title: "Limitaciones",
          content: "Más compleja de planificar, requiere experiencia para autoregular, puede ser confusa para principiantes, y dificulta medir progresión clara al variar constantemente.",
          keyPoints: [
            "Más compleja que periodización lineal",
            "Requiere experiencia y autoconocimiento",
            "Difícil medir progresión directa",
            "Puede confundir a principiantes",
            "Requiere más planificación"
          ]
        },
        {
          title: "Ejemplo Práctico Semanal (DUP)",
          content: "Ejemplo de periodización ondulante diaria para ejercicios principales en rutina Upper/Lower.",
          example: "Lunes - Upper (Fuerza):\n• Press banca: 5x5 @ 85%\n• Remo barra: 5x5 @ 85%\n• Press militar: 4x6 @ 80%\n• Dominadas: 4x6 @ 80%\n• Accesorios: 3x10-12\n\nMartes - Lower (Hipertrofia):\n• Sentadilla: 4x10 @ 70-75%\n• Peso muerto rumano: 4x10 @ 70-75%\n• Prensa: 3x12-15\n• Curl femoral: 3x12-15\n• Accesorios: 3x15-20\n\nJueves - Upper (Hipertrofia):\n• Press inclinado: 4x10 @ 70-75%\n• Remo mancuerna: 4x10 @ 70-75%\n• Press Arnold: 3x12\n• Jalón: 3x12\n• Accesorios: 3x12-15\n\nViernes - Lower (Fuerza):\n• Peso muerto: 5x5 @ 85%\n• Sentadilla frontal: 4x6 @ 80%\n• Prensa: 4x8 @ 75%\n• Peso muerto rumano: 3x8 @ 75%\n• Accesorios: 3x10-12\n\nProgresión: Aumentar carga cuando se completan todas las series en cada rango. Cada 4-6 semanas, rotar rangos (fuerza pasa a potencia 5x3, hipertrofia a fuerza-resistencia 4x8, etc.)."
        }
      ]
    }
  },
  {
    id: 9,
    title: "Análisis Comparativo",
    content: {
      introduction: "Comparación objetiva entre las principales metodologías para ayudarte a seleccionar la más adecuada según tu perfil, objetivos y contexto de entrenamiento.",
      sections: [
        {
          title: "Heavy Duty vs Sistema Weider",
          content: "Dos filosofías opuestas: mínimo volumen/máxima intensidad vs alto volumen/intensidad moderada. Heavy Duty busca el estímulo mínimo efectivo con recuperación prolongada. Weider busca acumulación de volumen con múltiples ángulos y técnicas.",
          keyPoints: [
            "Heavy Duty: 1-2 series al fallo absoluto por ejercicio",
            "Weider: 3-5 series por ejercicio, múltiples ejercicios",
            "Heavy Duty: Frecuencia baja (1x/semana por músculo)",
            "Weider: Frecuencia baja-media (1-2x/semana)",
            "Heavy Duty: Sesiones 30-45 min",
            "Weider: Sesiones 60-90 min"
          ],
          example: "Ejemplo Pecho:\n\nHeavy Duty:\n• Press banca: 1x6-8 al fallo + 2 forzadas\n• Press inclinado: 1x8-10 al fallo\nTotal: 2 series efectivas, 15 minutos\n\nWeider:\n• Press banca: 4x8-10\n• Press inclinado: 4x10-12\n• Aperturas: 3x12-15\n• Fondos: 3x10-12\n• Cruces: 3x15\nTotal: 17 series, 45-60 minutos",
          commonError: {
            error: "Mezclar ambas filosofías: hacer alto volumen Y llevar todo al fallo absoluto. Esto genera sobreentrenamiento garantizado.",
            correction: "Elige una filosofía: O bajo volumen con intensidad máxima (Heavy Duty), O alto volumen con intensidad moderada dejando RIR 1-3 (Weider). No puedes tener ambas."
          }
        },
        {
          title: "Alto Volumen vs Alta Intensidad",
          content: "El debate clásico. Alto volumen (Weider, PPL) acumula fatiga metabólica y daño muscular. Alta intensidad (Heavy Duty, HIT) maximiza tensión mecánica con mínimo volumen. Ambos funcionan, pero para perfiles diferentes.",
          keyPoints: [
            "Alto volumen: 15-20+ series/músculo/semana",
            "Alta intensidad: 6-10 series/músculo/semana",
            "Alto volumen: Mejor para hipertrofia pura",
            "Alta intensidad: Mejor para fuerza + eficiencia",
            "Alto volumen: Requiere buena recuperación",
            "Alta intensidad: Requiere intensidad mental real"
          ],
          example: "Espalda en una semana:\n\nAlto Volumen (PPL):\n• Pull 1: 9 series (3 ejercicios x 3 series)\n• Pull 2: 9 series (3 ejercicios x 3 series)\nTotal: 18 series/semana, 2 sesiones\n\nAlta Intensidad (Heavy Duty):\n• Sesión única: 3 series al fallo absoluto (dominadas, remo, pullover)\nTotal: 3 series/semana, 1 sesión\n\nResultado: Ambos pueden generar hipertrofia similar si la intensidad real es máxima en HIT y el volumen es tolerado en PPL.",
          commonError: {
            error: "Asumir que más volumen siempre es mejor. Muchos hacen 25+ series por músculo sin intensidad real, generando volumen basura.",
            correction: "Prioriza series efectivas. Si haces alto volumen, no todas las series deben ser al fallo. Si haces bajo volumen, cada serie debe ser realmente intensa (RIR 0-1)."
          }
        },
        {
          title: "Frecuencia Baja vs Alta",
          content: "Frecuencia baja (1x/semana - Weider, Heavy Duty) permite alto volumen o intensidad por sesión con recuperación prolongada. Frecuencia alta (2-3x/semana - Full Body, Upper/Lower, PPL) distribuye volumen y optimiza síntesis proteica.",
          keyPoints: [
            "Baja frecuencia: Permite especialización por sesión",
            "Alta frecuencia: Optimiza síntesis proteica (48-72h)",
            "Baja frecuencia: Mejor para volumen alto por músculo",
            "Alta frecuencia: Mejor para practicar técnica",
            "Baja frecuencia: Riesgo de pérdida de fuerza entre sesiones",
            "Alta frecuencia: Requiere gestión de fatiga acumulada"
          ],
          example: "Cuádriceps en 2 semanas:\n\nBaja Frecuencia (Weider):\n• Semana 1: 18 series (sentadilla, prensa, extensiones, zancadas)\n• Semana 2: 18 series\nTotal: 36 series en 2 sesiones\n\nAlta Frecuencia (Full Body):\n• 6 sesiones x 6 series = 36 series\nTotal: 36 series en 6 sesiones\n\nMismo volumen total, diferente distribución. Alta frecuencia permite mayor calidad por serie y mejor retención de fuerza.",
          commonError: {
            error: "Entrenar con alta frecuencia pero manteniendo el volumen de baja frecuencia. Ejemplo: PPL con 18 series de pecho por día, 2 veces/semana = 36 series/semana (excesivo).",
            correction: "Si aumentas frecuencia, reduce volumen por sesión. Regla: 12-20 series/músculo/semana es suficiente. Distribúyelas según tu frecuencia."
          }
        },
        {
          title: "Tabla Comparativa de Metodologías",
          content: "Resumen visual de las características principales de cada metodología para facilitar la selección.",
          keyPoints: [
            "Weider: Vol Alto, Frec Baja, Int Media, Tiempo Alto, Nivel Inter-Avanz",
            "Heavy Duty: Vol Bajo, Frec Muy Baja, Int Máxima, Tiempo Bajo, Nivel Avanzado",
            "HIT: Vol Bajo-Medio, Frec Media, Int Alta, Tiempo Medio, Nivel Princ-Inter",
            "PPL: Vol Alto, Frec Alta, Int Media-Alta, Tiempo Alto, Nivel Inter-Avanz",
            "Full Body: Vol Medio, Frec Alta, Int Media-Alta, Tiempo Medio, Nivel Todos",
            "Upper/Lower: Vol Medio-Alto, Frec Media, Int Media-Alta, Tiempo Medio, Nivel Inter-Avanz",
            "Lineal: Progresión estructurada, Nivel Princ-Inter, Objetivo Fuerza",
            "Ondulante: Variación constante, Nivel Inter-Avanz, Objetivo Mixto"
          ]
        },
        {
          title: "Recomendaciones de Selección",
          content: "Guía práctica para elegir metodología según tu perfil, objetivos y contexto de entrenamiento.",
          keyPoints: [
            "Principiante (0-1 año): Full Body 3x/semana o Upper/Lower 4x/semana",
            "Intermedio fuerza (1-3 años): Upper/Lower + Periodización Lineal",
            "Intermedio hipertrofia: PPL 6x/semana o Weider 5x/semana",
            "Avanzado eficiencia: Heavy Duty o HIT",
            "Avanzado volumen: PPL o Weider con periodización",
            "Tiempo limitado: Full Body 3x/semana o HIT",
            "Recuperación lenta: Heavy Duty o Full Body 2x/semana",
            "Estancamiento: Cambiar a Periodización Ondulante"
          ],
          example: "Casos prácticos:\n\n1. Juan, 25 años, 6 meses entrenando, objetivo masa muscular, 5 días disponibles:\nRecomendación: Upper/Lower 4 días + Periodización Lineal. Aún necesita frecuencia para técnica, pero puede manejar más volumen que Full Body.\n\n2. María, 32 años, 3 años entrenando, objetivo hipertrofia, 6 días disponibles:\nRecomendación: PPL 6 días. Experiencia suficiente, tiempo disponible, objetivo claro de hipertrofia.\n\n3. Carlos, 40 años, 5 años entrenando, recuperación lenta, 3 días disponibles:\nRecomendación: Full Body 3x/semana o Heavy Duty 2x/semana. Priorizar recuperación y eficiencia.\n\n4. Ana, 28 años, 2 años entrenando, estancada en fuerza:\nRecomendación: Cambiar a Periodización Ondulante (DUP) con Upper/Lower. La variación romperá el estancamiento."
        }
      ]
    }
  }
];

export const summaryData = {
  title: "Resumen Ejecutivo del Módulo",
  concepts: [
    "Dominas las diferencias filosóficas entre alto volumen vs alta intensidad",
    "Comprendes cómo frecuencia, volumen e intensidad se relacionan",
    "Conoces las ventajas y limitaciones reales de cada metodología",
    "Sabes seleccionar el sistema adecuado según perfil y objetivo",
    "Entiendes que no existe 'la mejor metodología', sino la más adecuada para ti",
    "Puedes diseñar rutinas basadas en principios, no en dogmas"
  ],
  application: [
    "Evalúa tu nivel actual, tiempo disponible y capacidad de recuperación",
    "Selecciona una metodología alineada con tu objetivo principal",
    "Implementa el sistema durante 8-12 semanas sin cambios",
    "Mide progresión objetiva (carga, repeticiones, volumen)",
    "Si no hay progresión en 4-6 semanas, ajusta variables o cambia metodología",
    "Recuerda: la consistencia supera a la metodología perfecta"
  ]
};

export const checklistData = {
  categories: [
    {
      title: "Selección de Metodología",
      items: [
        "He evaluado mi nivel de experiencia real (principiante, intermedio, avanzado)",
        "Conozco cuántos días por semana puedo entrenar de forma consistente",
        "He identificado mi objetivo principal (fuerza, hipertrofia, mixto)",
        "He evaluado mi capacidad de recuperación (rápida, media, lenta)",
        "He seleccionado una metodología alineada con mi perfil"
      ]
    },
    {
      title: "Implementación Práctica",
      items: [
        "He diseñado mi rutina semanal basada en la metodología elegida",
        "Conozco el volumen semanal por grupo muscular de mi rutina",
        "Sé qué intensidad relativa usar en cada sesión",
        "He planificado los descansos entre series según objetivo",
        "Tengo clara la progresión que seguiré (carga, reps, series)"
      ]
    },
    {
      title: "Comprensión de Principios",
      items: [
        "Entiendo la diferencia entre volumen efectivo y volumen basura",
        "Sé cuándo usar fallo muscular y cuándo dejarlo en reserva",
        "Comprendo cómo frecuencia afecta distribución de volumen",
        "Conozco las limitaciones de mi metodología elegida",
        "Sé cuándo y por qué cambiar de metodología"
      ]
    },
    {
      title: "Aplicación Avanzada",
      items: [
        "Puedo comparar objetivamente diferentes metodologías",
        "Sé adaptar una metodología a mis necesidades específicas",
        "Entiendo cómo periodizar dentro de mi metodología",
        "Puedo identificar señales de sobreentrenamiento",
        "Sé autoregular volumen e intensidad según recuperación"
      ]
    }
  ]
};

export const assessmentData = {
  questions: [
    {
      id: 1,
      question: "Un practicante intermedio con 4 días disponibles por semana y objetivo de hipertrofia equilibrada entre fuerza y masa muscular debería elegir:",
      options: [
        "Sistema Weider 5 días (no tiene tiempo suficiente)",
        "Upper/Lower 4 días con periodización ondulante",
        "Heavy Duty 2 días (frecuencia insuficiente para su nivel)",
        "Full Body 3 días (podría aprovechar mejor sus 4 días)"
      ],
      correctAnswer: 1,
      explanation: "Upper/Lower 4 días es ideal para este perfil: permite frecuencia óptima (2x/semana por músculo), volumen moderado-alto, y la periodización ondulante añade variedad para evitar estancamiento. Aprovecha los 4 días disponibles sin exceso."
    },
    {
      id: 2,
      question: "La principal diferencia entre Heavy Duty y HIT clásico es:",
      options: [
        "Heavy Duty usa más volumen que HIT",
        "HIT usa tempo lento y controlado, Heavy Duty prioriza carga máxima",
        "HIT es para principiantes, Heavy Duty para avanzados",
        "No hay diferencias significativas, son lo mismo"
      ],
      correctAnswer: 1,
      explanation: "HIT clásico (Arthur Jones) enfatiza tempo lento y controlado (4-2-4) con 1-3 series por ejercicio. Heavy Duty (Mentzer) prioriza carga máxima con 1 serie al fallo absoluto + técnicas de intensidad. Ambos son alta intensidad, pero con enfoques diferentes."
    },
    {
      id: 3,
      question: "Si un practicante hace PPL 6 días con 18 series de pecho por día de Push (36 series/semana), el problema principal es:",
      options: [
        "La frecuencia es demasiado baja",
        "El volumen total semanal es excesivo para la mayoría",
        "Debería hacer más series por día",
        "Necesita añadir un tercer día de Push"
      ],
      correctAnswer: 1,
      explanation: "36 series de pecho por semana es excesivo para la mayoría (12-20 es óptimo). Con PPL y frecuencia 2x/semana, debería hacer 6-10 series por día de Push, no 18. Más volumen no es mejor si supera capacidad de recuperación."
    },
    {
      id: 4,
      question: "La periodización ondulante (DUP) es superior a la lineal cuando:",
      options: [
        "Eres principiante y necesitas estructura simple",
        "Eres intermedio-avanzado y te estancas con progresión lineal",
        "Quieres maximizar fuerza máxima para competición",
        "Tienes poca experiencia en autoregulación"
      ],
      correctAnswer: 1,
      explanation: "DUP es ideal para intermedios-avanzados que se estancan con lineal. La variación frecuente evita adaptación específica y mantiene múltiples cualidades activas. Principiantes se benefician más de la simplicidad de periodización lineal."
    },
    {
      id: 5,
      question: "Un practicante avanzado con recuperación lenta, 3 días disponibles y objetivo de mantener masa mientras gana fuerza debería elegir:",
      options: [
        "PPL 6 días (no tiene tiempo ni recuperación)",
        "Weider 5 días (excesivo para su recuperación)",
        "Full Body 3 días con periodización lineal",
        "Upper/Lower 4 días (no tiene 4 días disponibles)"
      ],
      correctAnswer: 2,
      explanation: "Full Body 3 días es perfecto: se ajusta a sus 3 días disponibles, permite frecuencia alta (3x/semana) con volumen moderado por sesión (ideal para recuperación lenta), y la periodización lineal optimiza ganancia de fuerza. Eficiente y sostenible."
    }
  ]
};